import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  FilePondFile,
  FilePondBaseProps,
  FilePondOptions,
  FilePondLabelProps,
} from "filepond";
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileEncode from "filepond-plugin-file-encode";
import FilePondPluginFileMetadata from "filepond-plugin-file-metadata";
import FilePondPluginGetFile from "filepond-plugin-get-file";
import {
  Dialog,
  Button,
  TextField,
  Toast,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Text,
  FormHelperText,
  Space,
  Alert,
  IconName,
} from "@techzyapp/ui-lib";
import { filePondLabels } from "./labels";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import "filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css";

import "./styles.css";

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginFileValidateType,
  FilePondPluginFileEncode,
  FilePondPluginFileMetadata,
  FilePondPluginGetFile
);

export interface FileUploaderInitialFileProps {
  source: string | File;
  sortIndex: number;
  altText?: string;
}

export interface FileUploaderProps {
  initialFiles?: FileUploaderInitialFileProps[];
  onFilesChange?: (files: FileUploaderInitialFileProps[]) => void;
  title?: string;
  secondaryTitle?: string;
  secondaryTitleValue?: string;
  name?: FilePondBaseProps["name"];
  className?: FilePondBaseProps["className"];
  required?: FilePondBaseProps["required"];
  disabled?: FilePondBaseProps["disabled"];
  allowDrop?: FilePondBaseProps["allowDrop"];
  allowMultiple?: FilePondBaseProps["allowMultiple"];
  allowReorder?: FilePondBaseProps["allowReorder"];
  maxFiles?: FilePondBaseProps["maxFiles"];
  itemInsertLocation?: FilePondBaseProps["itemInsertLocation"];
  maxFileSize?: FilePondOptions["maxFileSize"];
  acceptedFileTypes?: FilePondOptions["acceptedFileTypes"];
  error?: boolean;
  helperText?: string;
  showAltTextAlert?: boolean;
  allowDownload?: boolean;
  iconName?: IconName;
}

const FileUploader = ({
  initialFiles = [],
  onFilesChange,
  title = "Gallery",
  secondaryTitle = "Recommended Size",
  secondaryTitleValue = "",
  name = "filepond",
  className = null,
  required = false,
  disabled = false,
  allowDrop = true,
  allowMultiple = false,
  allowReorder = true,
  maxFiles = null,
  itemInsertLocation = "after",
  maxFileSize = "5MB",
  acceptedFileTypes = [],
  error,
  helperText,
  showAltTextAlert = true,
  allowDownload = false,
  iconName = "Camera",
}: FileUploaderProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("Edit Alt Text");
  const [altText, setAltText] = useState("");
  const [currentFile, setCurrentFile] = useState<FilePondFile | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(showAltTextAlert);
  const filePondRef = useRef<HTMLDivElement | null>(null);
  const [preventDialog, setPreventDialog] = useState(false);

  const handleDialogOpen = (fileName: string) => {
    // if (preventDialog) return
    setDialogTitle(fileName);
    setOpenDialog(true);
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest(".filepond--download-icon")) {
        setPreventDialog(true);
        event.stopPropagation();
      } else {
        setPreventDialog(false);
      }
    };

    filePondRef.current &&
      filePondRef.current.addEventListener("click", handleClick);

    return () => {
      filePondRef.current &&
        filePondRef.current.removeEventListener("click", handleClick);
    };
  }, []);

  const handleDialogClose = () => setOpenDialog(false);

  const sortedInitialFiles = [...initialFiles].sort(
    (a, b) => a.sortIndex - b.sortIndex
  );

  const [files, setFiles] = useState<any[]>(sortedInitialFiles);
  const [fileObjects, setFileObjects] =
    useState<FileUploaderInitialFileProps[]>(sortedInitialFiles);

  const handleFileObjects = (fileItems: FilePondFile[]) => {
    const objects = fileItems.map((item, index) => {
      const source =
        item.file instanceof File ? item.file : (item.source as string);
      const existingObject = fileObjects.find((obj) => obj.source === source);
      return {
        source,
        sortIndex: index,
        altText: existingObject?.altText || "Default Alt Text",
      };
    });
    setFileObjects(objects);
  };

  const handleRemoveFile = (error: any, file: FilePondFile) => {
    if (!error) {
      setFiles((prevFiles) => prevFiles.filter((f) => f !== file.file));
    }
  };

  useEffect(() => {
    onFilesChange && onFilesChange(fileObjects);
  }, [fileObjects]);

  useEffect(() => {
    handleFileObjects(files);
  }, [files]);

  const handleDialogSave = () => {
    if (currentFile) {
      const fileSource =
        currentFile.file instanceof File
          ? currentFile.file
          : currentFile.source;
      setFileObjects((prevObjects) =>
        prevObjects.map((obj) =>
          obj.source === fileSource
            ? { ...obj, altText: altText || "Default Alt Text" }
            : obj
        )
      );
      handleDialogClose();
      setAltText("");
      setCurrentFile(null);
      Toast.success("Alt Text Successfully Saved");
    }
  };

  return (
    <div
      ref={filePondRef}
      className={clsx("filepond-container", {
        ["filepond-disabled"]: disabled,
        ["filepond-error"]: error,
        ["filepond-single"]: !allowMultiple,
        ["filepond-allow-download"]: allowDownload,
      })}
    >
      <ListItem>
        <ListItemAvatar>
          <Avatar size="lg" iconName={iconName}></Avatar>
        </ListItemAvatar>
        <ListItemText>
          <Text color="primary" weight="medium">
            {title}
          </Text>
          <Text size="xs">
            {secondaryTitle}
            {secondaryTitleValue && ": " + secondaryTitleValue}
          </Text>
        </ListItemText>
      </ListItem>
      <FilePond
        {...filePondLabels}
        name={name}
        className={className}
        required={required}
        disabled={disabled}
        allowDrop={allowDrop}
        allowMultiple={allowMultiple}
        allowReorder={allowReorder}
        maxFiles={maxFiles}
        itemInsertLocation={itemInsertLocation}
        files={files}
        maxFileSize={maxFileSize}
        acceptedFileTypes={acceptedFileTypes}
        allowFileEncode={true}
        onremovefile={handleRemoveFile}
        onupdatefiles={setFiles}
        onreorderfiles={(fileItems) => {
          setFiles(fileItems);
          handleFileObjects(fileItems);
        }}
        onactivatefile={(file) => {
          const fileSource =
            file.file instanceof File ? file.file : file.source;
          setCurrentFile(file);
          setAltText(
            fileObjects.find((obj) => obj.source === fileSource)?.altText || ""
          );
          !preventDialog && handleDialogOpen(file.file.name as string);
        }}
      />
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        title={dialogTitle}
        size="sm"
        actions={
          <>
            <Button
              onClick={handleDialogClose}
              variant="transparent"
              color="primary"
            >
              Cancel
            </Button>
            <Button onClick={handleDialogSave}>Save</Button>
          </>
        }
      >
        <TextField
          label="Alt Text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
        />
      </Dialog>
      {helperText && (
        <>
          {fileObjects.length ? <Space /> : null}
          <FormHelperText error={error}>{helperText}</FormHelperText>
        </>
      )}
      {fileObjects.length && !disabled && showAlert ? (
        <>
          <Space />
          <Alert severity="info" onClose={() => setShowAlert(false)}>
            Click the image, for change 'Alt Text'
          </Alert>
        </>
      ) : null}
    </div>
  );
};

export default FileUploader;
